/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/TabsPage';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  BaseComponentItem,
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue, TabItem, WrapperProps } from './TabItem';

const name = 'Tab Item';

const TabItemManager: ComponentManagerFn<'TabItem', WrapperProps> = (
  platform: Platform,
): ComponentManager<'TabItem', WrapperProps> => {
  return {
    ...baseComponentManager<'TabItem', WrapperProps>(platform, 'TabItem'),
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          ...getCustomChildItemTypes('TabItem'),
          'ActionButton',
          'BoxContainer',
          'Button',
          'ButtonDropdown',
          'CanvasGrid',
          'Card',
          'Chartjs',
          'Code',
          'CombinationChart',
          'DataGrid',
          'DataStoreReference',
          'EmbedPage',
          'FAB',
          'FlexPanel',
          'Form',
          'HorizontalSplitPanel',
          'IconButton',
          'IoTImageMapper',
          'Markdown',
          'Mirror',
          'Paper',
          'ProgressIndicator',
          'PulseTip',
          'RichTextEditor',
          'RowPanel',
          'SearchForm',
          'SmartSearch',
          'SpeedDial',
          'SuspendPage',
          'TabPanel',
          'Toolbar',
          'TriggerOnTabActive',
          'TriggerOnTabExit',
          'VerticalSplitPanel',
          'WorkflowViewer',
          'TriggerOnClick',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    props: {
      label: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      hotKey: {
        label: 'Hot Key',
        type: 'string',
        maxLength: 1,
        helpText: (item: RequiredItem<'TabItem'>) => {
          const key =
            item.props.hotKey ||
            (item.props.label.type === 'text' && item.props.label.value?.length
              ? item.props.label.value.charAt(0)
              : '<Hot Key>');

          return `A shortcut key to be mapped for this tab item click event. User can use Ctrl+Shift+${key} or Cmd+Shift+${key} as a keyboard shortcut instead of a mouse click on this tab item.`;
        },
      },
      wrapped: {
        label: 'Wrapped',
        type: 'boolean',
        helpText: 'If checked the label will be wrapped',
      },
      useImageAsIcon: {
        label: 'Use Image as Icon',
        type: 'boolean',
        helpText: 'Use image as Tab Icon',
      },
      icon: {
        label: 'Start Icon',
        type: 'iconselect',
      },
      image: {
        label: 'Start Image',
        type: 'ImgAttributeChooser',
      },
      imageHeight: {
        label: 'Start Image Height',
        type: 'number',
      },
      imageWidth: {
        label: 'Start Image Width',
        type: 'number',
      },
      indicatorColor: {
        label: 'Indicator Color',
        type: 'ThemeBGColor',
        filterColor: (color) => color !== 'paper',
        helpText:
          'The color of tab header text and active indicator when selected. Choose Inherit to hide the active indicator and set the tab header text color to text.primary.',
      },
      subtitle: {
        label: 'Sub Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      badge: {
        label: 'Badge',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'The badge /tag for the tab',
      },
    },
    name,
    canHaveChildren: () => true,
    category: 'Containers',
    excludeChildren: true,
    updateMetaData: (
      { itemId }: BaseComponentItem,
      data: Required<TabItem>,
    ) => ({
      ...data,
      value: itemId,
    }),
    getWrapperProps: ({ item }) => ({ value: item.itemId }),
    allowClick: true,
    layout: 'sizeAware',
    getDisplayText: (item) => item.props.label.value,
    generateShortId: (item) =>
      item.label?.type === 'text' ? item.label.value : undefined,
    icon: { iconName: 'io-tab-item', iconType: 'cloudio' },
    appendOverlay: true, // tab item button's first child has bottom margin and hence move the overlay toward the end
    description: 'Check Tab Panel&apos;s documentation for details about Tabs.',
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'image':
        case 'imageHeight':
        case 'imageWidth':
          return props.useImageAsIcon;
        case 'icon':
          return !props.useImageAsIcon;
        default:
          return true;
      }
    },
    helpRenders: [
      {
        title: 'Basic Tab',
        description: 'Tab with a Title & Icon',
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: ['icon', 'label'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Indicator Color',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['indicatorColor'],
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            indicatorColor: 'success',
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Sub Title',
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['subtitle'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            subtitle: { type: 'text', value: 'Tab with a sub title!' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Dynamic Title & Sub Title using Expression',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['label', 'subtitle'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'expression', value: '$.session.displayName' },
            icon: { iconName: 'apple', iconType: 'brands' },
            subtitle: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Hi ${$.session.displayName}`',
            },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
      {
        title: 'Tab with Badge',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['badge'],
        clearParentChildren: true,
        item: {
          itemId: 'tabItem',
          props: {
            label: { type: 'text', value: 'Tab One' },
            icon: { iconName: 'apple', iconType: 'brands' },
            badge: { type: 'expression', value: '11 * 11' },
          },
          children: [],
          type: 'TabItem',
          parent: 'tabPanel',
        },
      },
    ],
  };
};

export default TabItemManager;
